/**
 * Centralized Zod schemas – re-exported from @saee/shared.
 * Security Checklist: Frontend and Backend use the EXACT same schema (see shared package).
 * Use with react-hook-form (zodResolver) and validate before apiClient.post/patch.
 *
 * When VITE_BYPASS_NATIONAL_ID_VALIDATION=true, national ID schemas accept any non-empty
 * string. Backend must also have BYPASS_NATIONAL_ID_VALIDATION=true for end-to-end bypass.
 */

import { z } from 'zod';
import {
  emailSchema,
  looseSaudiNationalIdSchema,
  saudiNationalIdSchema as _strictNationalIdSchema,
  signUpCompleteFormSchema as _signUpCompleteFormSchema,
  createBrokerBodySchema as _createBrokerBodySchema,
} from '@saee/shared';

const _bypass = import.meta.env.VITE_BYPASS_NATIONAL_ID_VALIDATION === 'true';

export const saudiNationalIdSchema = _bypass ? looseSaudiNationalIdSchema : _strictNationalIdSchema;

const _dobField = z
  .string()
  .min(10, 'auth.validation.dobRequired')
  .refine((v) => {
    const d = new Date(v + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return false;
    return d <= new Date();
  }, 'auth.validation.dobInvalid');

export const signUpCompleteFormSchema = _bypass
  ? z.object({ idNumber: looseSaudiNationalIdSchema, dob: _dobField, email: emailSchema })
  : _signUpCompleteFormSchema;

export const createBrokerBodySchema = _bypass
  ? z.object({
      name: z.string().min(1, 'admin.brokerForm.nameRequired').trim(),
      mobile: z
        .string()
        .min(1, 'admin.brokerForm.mobileRequired')
        .transform((v) => (v ?? '').replace(/\D/g, '').slice(-9))
        .refine((digits) => /^5[0-9]{8}$/.test(digits), 'auth.validation.mobileInvalid'),
      nationalId: looseSaudiNationalIdSchema,
      dateOfBirth: _dobField,
      email: emailSchema,
      falLicenseNumber: z.string().optional().nullable(),
      iban: z.string().optional().nullable(),
    })
  : _createBrokerBodySchema;

export {
  emailSchema,
  saudiNationalIdChecksumValid,
  registerFormSchema,
  authRegisterBodySchema,
  authOtpContextSchema,
  otpFormSchema,
  authVerifyOtpBodySchema,
  authCompleteBodySchema,
  authNafathInitiateResponseSchema,
  authNafathStatusResponseSchema,
  nafathVerificationStatusSchema,
  authNationalAddressBodySchema,
  inviteAdminFormSchema,
  createAdminAccountBodySchema,
  updateAdminAccountBodySchema,
  updateAdminSettingsBodySchema,
  brokerInternalNoteBodySchema,
  createDealBrokerEntrySchema,
  createDealBodySchema,
  addDealBrokerBodySchema,
  updateDealBrokerBodySchema,
  requestRefundBodySchema,
  payerRefundConfirmBodySchema,
  cancelDealBodySchema,
  rejectRefundBodySchema,
  brokerApproveBodySchema,
  refundApproveBodySchema,
  brokerRejectBodySchema,
  userProfileDisplaySchema,
  userProfileEmailUpdateSchema,
  profilePatchBodySchema,
  ibanFormSchema,
  ibanBodySchema,
  falLicenseFormSchema,
  profileFalBodySchema,
  newBrokerageStep1Schema,
  newBrokerageStep2Schema,
  newBrokerageStep3Schema,
  newBrokerageRequestSchema,
  commissionSplitRowSchema,
  commissionSplitRowsSchema,
  authAddressSelectionSchema,
  walletWithdrawBodySchema,
  commissionLogTransferBodySchema,
  platformResetBodySchema,
} from '@saee/shared';

export type {
  RegisterFormValues,
  OtpFormValues,
  SignUpCompleteFormValues,
  InviteAdminFormValues,
  CreateAdminAccountBody,
  UpdateAdminAccountBody,
  UpdateAdminSettingsBody,
  CreateBrokerBody,
  BrokerInternalNoteBody,
  CreateDealBody,
  CreateDealBrokerEntry,
  AddDealBrokerBody,
  UpdateDealBrokerBody,
  RequestRefundBody,
  CancelDealBody,
  RejectRefundBody,
  BrokerApproveBody,
  BrokerRejectBody,
  UserProfileDisplay,
  UserProfileEmailUpdate,
  IbanFormValues,
  FalLicenseFormValues,
  NewBrokerageRequest,
  CommissionSplitRow,
  CommissionSplitRows,
  AuthAddressSelection,
  PlatformResetBody,
} from '@saee/shared';
